import dayjs, { Dayjs } from 'dayjs';

import {
  PolishPluralForm,
  polishPluralForm,
} from '@/views/processes/offer/components/aiChat/util/offerAiQuestion.util.ts';

export type SmartDateKey =
  'today' | 'tomorrow' | 'yearAgo' | 'yesterday' | `${PluralKey}.${PolishPluralForm}`;

export type SmartDateTone = 'default' | 'muted' | 'overdue' | 'today';

export type SmartDateTranslate = (key: SmartDateKey, options?: { count: number }) => string;

export type SmartDateVariant = 'calendar' | 'due' | 'relative';

export type SmartDateView = {
  label: string;
  tone: SmartDateTone;
  tooltip: string;
};

type PluralKey = 'daysAgo' | 'inDays' | 'monthsAgo' | 'overdueBy' | 'weeksAgo';

const MIN_MEANINGFUL_YEAR = 1971;

const hasTime = (date: Dayjs) => date.hour() !== 0 || date.minute() !== 0;

const dayDifference = (date: Dayjs, now: Dayjs) =>
  date.startOf('day').diff(now.startOf('day'), 'day');

const plural = (t: SmartDateTranslate, key: PluralKey, count: number): string =>
  t(`${key}.${polishPluralForm(count)}`, { count });

export const calendarDateLabel = (
  date: Dayjs,
  now: Dayjs,
  t: SmartDateTranslate,
  options: { absolute?: boolean; withTime: boolean },
): string => {
  const diff = dayDifference(date, now);
  let label: string;
  if (!options.absolute && diff === 0) {
    label = t('today');
  } else if (!options.absolute && diff === -1) {
    label = t('yesterday');
  } else if (!options.absolute && diff === 1) {
    label = t('tomorrow');
  } else if (date.year() === now.year()) {
    label = date.format('D MMM');
  } else {
    label = date.format('D MMM YYYY');
  }
  return options.withTime && hasTime(date) ? `${label}, ${date.format('HH:mm')}` : label;
};

const relativeLabel = (date: Dayjs, now: Dayjs, t: SmartDateTranslate): string => {
  const days = -dayDifference(date, now);
  if (days <= 0) {
    return t('today');
  }
  if (days < 14) {
    return plural(t, 'daysAgo', days);
  }
  if (days < 60) {
    return plural(t, 'weeksAgo', Math.floor(days / 7));
  }
  if (days < 365) {
    return plural(t, 'monthsAgo', Math.floor(days / 30));
  }
  return t('yearAgo');
};

export const fullDateLabel = (date: Dayjs): string =>
  date.format(hasTime(date) ? 'dddd, D MMMM YYYY, HH:mm' : 'dddd, D MMMM YYYY');

export const resolveSmartDate = (params: {
  date?: string | null;
  isFinished?: boolean;
  now?: Dayjs;
  t: SmartDateTranslate;
  variant: SmartDateVariant;
  withTime?: boolean;
}): SmartDateView | null => {
  const { date: rawDate, isFinished, t, variant, withTime = true } = params;
  const now = params.now ?? dayjs();
  if (!rawDate) {
    return null;
  }
  const date = dayjs(rawDate);
  if (!date.isValid() || date.year() < MIN_MEANINGFUL_YEAR) {
    return null;
  }
  const tooltip = fullDateLabel(date);

  if (variant === 'relative') {
    return { label: relativeLabel(date, now, t), tone: 'default', tooltip };
  }

  if (variant === 'due' && !isFinished) {
    const diff = dayDifference(date, now);
    if (diff < 0) {
      return {
        label: calendarDateLabel(date, now, t, { withTime }),
        tone: 'overdue',
        tooltip: `${plural(t, 'overdueBy', -diff)} · ${tooltip}`,
      };
    }
    if (diff === 0) {
      return { label: calendarDateLabel(date, now, t, { withTime }), tone: 'today', tooltip };
    }
    if (diff > 1 && diff < 7) {
      return { label: plural(t, 'inDays', diff), tone: 'default', tooltip };
    }
  }

  return {
    label: calendarDateLabel(date, now, t, { withTime }),
    tone: variant === 'due' && isFinished ? 'muted' : 'default',
    tooltip,
  };
};
